import { useState, useCallback, useEffect } from 'react';
import { sendChatMessage, getConversationHistory } from '@/lib/api';
import type { Message } from '@/lib/types';

interface UseChatOptions {
  learnerId: string;
  moduleId: string;
  partId: string;
  phase: 'companion' | 'assessment' | 'reflection' | 'final';
  slideIndex?: number;
  slideTitle?: string;
}

export function useChat({ learnerId, moduleId, partId, phase, slideIndex, slideTitle }: UseChatOptions) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Load conversation history from backend on mount (handles page refresh resume)
  useEffect(() => {
    let cancelled = false;
    const loadHistory = async () => {
      try {
        const history = await getConversationHistory(learnerId, moduleId, partId, phase);
        if (!cancelled && history.length > 0) {
          setMessages(history);
        }
      } catch (err) {
        console.error('Failed to load chat history:', err);
      }
    };
    loadHistory();
    return () => { cancelled = true; };
  }, [learnerId, moduleId, partId, phase]);

  const sendMessage = useCallback(
    async (userMessage: string) => {
      if (!userMessage.trim() || isLoading) return;

      setIsLoading(true);
      setError(null);
      setStreamingMessage('');

      // Add user message immediately
      const newUserMessage: Message = {
        role: 'user',
        content: userMessage,
      };
      setMessages((prev) => [...prev, newUserMessage]);

      try {
        const stream = await sendChatMessage({
          learnerId,
          moduleId,
          partId,
          phase,
          message: userMessage,
          slideIndex,
          slideTitle,
        });

        const reader = stream.getReader();
        const decoder = new TextDecoder();
        let assistantContent = '';
        let buffer = '';
        let streamFinished = false;

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            // Process complete lines (SSE format)
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6).trim();

                if (data === '[DONE]') {
                  // Stream finished - add final message and stop
                  streamFinished = true;
                  if (assistantContent) {
                    const assistantMessage: Message = {
                      role: 'assistant',
                      content: assistantContent,
                    };
                    setMessages((prev) => [...prev, assistantMessage]);
                  }
                  setStreamingMessage('');
                  setIsLoading(false);
                  break;
                }

                if (!data) continue;

                try {
                  const parsed = JSON.parse(data);

                  if (parsed.type === 'text') {
                    assistantContent += parsed.content;
                    setStreamingMessage(assistantContent);
                  } else if (parsed.type === 'error') {
                    throw new Error(parsed.content);
                  } else if (parsed.type === 'tool_result') {
                    // Tool result - you could display this differently if desired
                    console.log('Tool result:', parsed.name, parsed.result);
                  }
                } catch (e) {
                  console.error('Failed to parse SSE data:', data, e);
                }
              }
            }

            // Break if stream finished
            if (streamFinished) break;
          }
        } finally {
          // Always release the reader
          reader.releaseLock();
        }

        // Clean up: ensure loading state is off and streaming message is cleared
        setIsLoading(false);
        setStreamingMessage('');
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to send message');
        setError(error);
        setIsLoading(false);
        setStreamingMessage('');
        console.error('Chat error:', error);
      }
    },
    [learnerId, moduleId, partId, phase, slideIndex, slideTitle, isLoading]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setStreamingMessage('');
    setError(null);
  }, []);

  const turnCount = messages.filter((m) => m.role === 'user').length;

  return {
    messages,
    streamingMessage,
    isLoading,
    error,
    turnCount,
    sendMessage,
    clearMessages,
  };
}
